import React, { useEffect } from 'react';

// Business Information Constants
export const BUSINESS_INFO = {
  name: "Swift Move and Clean",
  legalName: "Swift Move and Clean LLC",
  phone: "(812) 669-4165",
  phoneFormatted: "812-669-4165",
  email: "info@swiftmoveandclean.com",
  address: {
    street: "130 W Brunswick Drive",
    city: "Columbus",
    state: "Indiana",
    stateCode: "IN",
    zip: "47201",
    country: "US"
  },
  geo: {
    latitude: "39.2014",
    longitude: "-85.9214"
  },
  hours: "Mon-Sat 8AM-7PM",
  hoursSpecification: [
    { dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], opens: "08:00", closes: "19:00" }
  ],
  url: "https://swiftmoveandclean.com",
  logo: "https://customer-assets.emergentagent.com/job_swift-movers-1/artifacts/3vyram8e_Gemini_Generated_Image_tphblstphblstphb.png",
  priceRange: "$$",
  foundingDate: "2023",
  serviceAreas: [
    { name: "Columbus", state: "Indiana" },
    { name: "Indianapolis", state: "Indiana" },
    { name: "Carmel", state: "Indiana" },
    { name: "Fishers", state: "Indiana" },
    { name: "Greenwood", state: "Indiana" },
    { name: "Avon", state: "Indiana" },
    { name: "Plainfield", state: "Indiana" },
    { name: "Zionsville", state: "Indiana" },
    { name: "Franklin", state: "Indiana" },
    { name: "Bloomington", state: "Indiana" },
    { name: "Seymour", state: "Indiana" },
    { name: "Shelbyville", state: "Indiana" },
    { name: "Greensburg", state: "Indiana" },
    { name: "North Vernon", state: "Indiana" },
    { name: "Sellersburg", state: "Indiana" },
    { name: "Nashville", state: "Indiana" },
    { name: "Danville", state: "Indiana" },
    { name: "Louisville", state: "Kentucky" },
    { name: "Cincinnati", state: "Ohio" }
  ],
  socialProfiles: {
    facebook: "https://www.facebook.com/share/1DLPnAN9je/",
    instagram: "https://www.instagram.com/swiftmoveandclean",
    twitter: "https://x.com/swiftmoveclean",
    linkedin: "https://www.linkedin.com/company/swift-move-and-clean"
  },
  ratings: {
    google: { value: 5.0, count: 500 },
    yelp: { value: 4.8, count: 150 }
  }
};

// LocalBusiness Schema
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${BUSINESS_INFO.url}/#organization`,
  "name": BUSINESS_INFO.name,
  "legalName": BUSINESS_INFO.legalName,
  "image": BUSINESS_INFO.logo,
  "logo": BUSINESS_INFO.logo,
  "url": BUSINESS_INFO.url,
  "telephone": BUSINESS_INFO.phone,
  "email": BUSINESS_INFO.email,
  "priceRange": BUSINESS_INFO.priceRange,
  "foundingDate": BUSINESS_INFO.foundingDate,
  "description": "Swift Move and Clean is a licensed and fully insured professional moving and cleaning company serving Columbus, Indianapolis, communities across Indiana, and select nearby metro areas including Louisville and Cincinnati. We offer residential moving, commercial moving, packing services, move-in/move-out cleaning, deep cleaning, and post-construction cleaning.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": BUSINESS_INFO.address.street,
    "addressLocality": BUSINESS_INFO.address.city,
    "addressRegion": BUSINESS_INFO.address.stateCode,
    "postalCode": BUSINESS_INFO.address.zip,
    "addressCountry": BUSINESS_INFO.address.country
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": BUSINESS_INFO.geo.latitude,
    "longitude": BUSINESS_INFO.geo.longitude
  },
  "openingHoursSpecification": BUSINESS_INFO.hoursSpecification.map(spec => ({
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": spec.dayOfWeek,
    "opens": spec.opens,
    "closes": spec.closes
  })),
  "areaServed": BUSINESS_INFO.serviceAreas.map((area) => ({
    "@type": "City",
    "name": area.name,
    "containedInPlace": {
      "@type": "State",
      "name": area.state
    }
  })),
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Moving and Cleaning Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Residential Moving",
          "description": "Professional residential moving services for homes, apartments, and condos across Indiana"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Commercial Moving",
          "description": "Office and commercial moving services for businesses in Indiana"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Packing Services",
          "description": "Professional packing and unpacking services for moves"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Move-In/Move-Out Cleaning",
          "description": "Deep cleaning services for move-in and move-out situations"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Deep Cleaning",
          "description": "Comprehensive deep cleaning services for homes and offices"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Commercial Cleaning",
          "description": "Professional cleaning services for offices and commercial spaces"
        }
      }
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": "500",
    "bestRating": "5",
    "worstRating": "1"
  },
  "sameAs": Object.values(BUSINESS_INFO.socialProfiles)
});

// Service Schema Generator
export const getServiceSchema = (serviceName, serviceDescription, serviceUrl) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": serviceName,
  "name": serviceName,
  "description": serviceDescription,
  "url": serviceUrl,
  "provider": {
    "@type": "LocalBusiness",
    "name": BUSINESS_INFO.name,
    "telephone": BUSINESS_INFO.phone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": BUSINESS_INFO.address.street,
      "addressLocality": BUSINESS_INFO.address.city,
      "addressRegion": BUSINESS_INFO.address.stateCode,
      "postalCode": BUSINESS_INFO.address.zip
    }
  },
  "areaServed": {
    "@type": "State",
    "name": "Indiana"
  }
});

// Breadcrumb Schema Generator
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// FAQ Schema Generator
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// SEO Component
export const SEO = ({ 
  title, 
  description, 
  keywords,
  canonical,
  ogType = "website",
  ogImage,
  schema = [],
  noindex = false
}) => {
  const siteTitle = "Swift Move and Clean | Professional Movers & Cleaners in Indiana and Nearby Metro Areas";
  const fullTitle = title
    ? (title.includes("Swift Move and Clean") ? title : `${title} | Swift Move and Clean`)
    : siteTitle;
  const defaultDescription = "Swift Move and Clean offers professional moving and cleaning services in Columbus, Indianapolis, across Indiana, and nearby metro areas like Louisville and Cincinnati. Licensed, insured movers. Free quotes. Call (812) 669-4165.";
  const metaDescription = description || defaultDescription;
  const defaultKeywords = "movers Columbus IN, moving company Indianapolis, cleaners near me, move-out cleaning Indiana, commercial movers Carmel, residential moving Fishers, packing services Bloomington";
  const metaKeywords = keywords || defaultKeywords;
  const defaultImage = BUSINESS_INFO.logo;
  const resolvedOgImage = ogImage && ogImage.startsWith('/')
    ? `${BUSINESS_INFO.url}${ogImage}`
    : ogImage;
  const metaImage = resolvedOgImage || defaultImage;
  const baseUrl = BUSINESS_INFO.url;
  const canonicalPath = canonical || "/";
  const canonicalUrl = canonicalPath.startsWith('http')
    ? canonicalPath
    : `${baseUrl}${canonicalPath}`;

  // Combine all schemas into a single JSON-LD @graph payload. react-helmet-async
  // has a known quirk where mapping variable-length arrays of <script> children
  // inside <Helmet> can fail to flush; using a single @graph script avoids it
  // and is Google's recommended pattern for multiple schemas on one page.
  const allSchemas = [getLocalBusinessSchema(), ...schema];
  const graphPayload = {
    "@context": "https://schema.org",
    "@graph": allSchemas.map((s) => {
      // Strip per-schema @context since @graph provides it at the root
      // eslint-disable-next-line no-unused-vars
      const { "@context": _ctx, ...rest } = s || {};
      return rest;
    })
  };

  // Belt-and-suspenders: force document.title on the client even if Helmet's
  // declarative child flush ever races with the static index.html <title>.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = fullTitle;
    }
  }, [fullTitle]);

  return (
    <>
      {/* React 19 natively hoists these head-relevant tags to <head>.
          We keep the component as a Fragment (no Helmet wrapper). */}

      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />

      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={BUSINESS_INFO.name} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Geographic */}
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.placename" content="Columbus, Indiana" />
      <meta name="geo.position" content={`${BUSINESS_INFO.geo.latitude};${BUSINESS_INFO.geo.longitude}`} />
      <meta name="ICBM" content={`${BUSINESS_INFO.geo.latitude}, ${BUSINESS_INFO.geo.longitude}`} />

      {/* Business */}
      <meta name="author" content={BUSINESS_INFO.name} />
      <meta name="contact" content={BUSINESS_INFO.email} />
      <meta name="reply-to" content={BUSINESS_INFO.email} />

      {/* Mobile */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={BUSINESS_INFO.name} />

      {/* Structured Data — single @graph payload (Google-preferred multi-schema pattern) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(graphPayload) }}
      />
    </>
  );
};

export default SEO;
